import { genericMemo } from "@redotech/react-util/component";
import { RegisteredElements } from "@redotech/redo-email-content/element-registrars/registered-elements";
import { ScreenSize } from "@redotech/redo-marketing-templates-common/email-builder";
import { ReactNode } from "react";

export interface WidgetProps<E> {
  element: E;
}

/**
 * Decorator pattern: wraps FormElementProps to extend the functionality of a component
 * with editing-specific functionality like changing position, editing settings, etc.
 */
export interface WidgetEditorProps<T extends WidgetProps<E>, E> {
  widget: T;
  setElement: (element: E) => void;
  onSelectElement: (
    elementIndex: number | null,
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => void;
  selected: boolean;
  selectedColumnIndex?: number | null;
  setSelectedColumnIndex?: (index: number | null) => void;
  index: number;
  sectionToolbar?: React.ReactNode;
  /**
   * If true, the section toolbar will not be rendered,
   * but merely passed down to the child, who can render it however they want.
   */
  skipRenderingSectionToolbar?: boolean;
  screenSize?: ScreenSize;
  interactable?: boolean;
  renderFadeSeconds?: number;
  registeredElements?: RegisteredElements;
  /** If true, the wrapper will stretch to fill the parent's height */
  stretch?: boolean;
  /** Whether this element is currently being hovered */
  hovered?: boolean;
  /** Callback when hover state changes, called with elementId or null */
  onHover?: (elementId: string | null) => void;
  /** Element ID for hover/selection tracking in hierarchical editors */
  elementId?: string;
  /** Callback to delete this element */
  onDelete?: () => void;
  /** Whether the toolbar should be active (for multi-device previews where only one shows toolbar) */
  isToolbarActive?: boolean;
}

export type WidgetEditorDecorator<T extends WidgetProps<E>, E> = {
  children: (props: WidgetEditorProps<T, E>) => ReactNode;
} & WidgetEditorProps<T, E>;

export const withEditor = <T extends WidgetProps<E>, E>(
  Decorator: React.FC<WidgetEditorDecorator<T, E>>,
  Component: React.FC<T>,
) => {
  return genericMemo(function FormElementEditor(
    props: WidgetEditorProps<T, E>,
  ) {
    return (
      <Decorator {...props}>{() => <Component {...props.widget} />}</Decorator>
    );
  });
};
